import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Store, Plus, DollarSign, ShoppingBag, Users, TrendingUp,
  ExternalLink, Sparkles, Trash2,
} from "lucide-react";
import DashboardShell from "@/pages/dashboard/DashboardShell";
import { useAuth } from "@/lib/auth";
import { monetize, TEMPLATE_LABELS, formatPrice } from "@/lib/monetize";
import { extractFormError } from "@/lib/api";

function StatCard({ label, value, hint, testid }) {
  return (
    <div data-testid={testid} className="border border-neutral-200 bg-white p-5 sm:p-6">
      <p className="text-[10px] uppercase tracking-[0.22em] text-neutral-400">{label}</p>
      <p className="font-serif text-3xl sm:text-4xl mt-2 text-aurora-static">{value}</p>
      {hint && <p className="text-xs text-neutral-500 mt-2">{hint}</p>}
    </div>
  );
}

function StorefrontCard({ sf, onDelete }) {
  const link = `/store/${sf.slug}`;
  return (
    <div
      data-testid={`monetize-store-${sf.id}`}
      className="group border border-neutral-200 bg-white p-6 hover:border-gold/40 transition"
    >
      <div className="flex items-start justify-between gap-3">
        <Store size={18} className="text-gold" strokeWidth={1.4} />
        <span className={`text-[10px] uppercase tracking-[0.2em] border px-2 py-0.5 ${sf.published ? "text-emerald-300 border-emerald-400/40" : "text-neutral-400 border-neutral-200"}`}>
          {sf.published ? "Live" : "Draft"}
        </span>
      </div>
      <h3 className="font-serif text-2xl mt-4 leading-tight">{sf.title}</h3>
      {sf.tagline && <p className="text-sm text-neutral-500 mt-1.5 leading-relaxed">{sf.tagline}</p>}
      <p className="text-xs text-neutral-400 mt-3 font-mono truncate">goldkey.studio/store/{sf.slug}</p>
      <div className="flex items-center gap-3 mt-6 pt-5 border-t border-neutral-200/70">
        <Link
          to={`/dashboard/monetize/store/${sf.id}`}
          data-testid={`monetize-manage-${sf.id}`}
          className="text-xs uppercase tracking-[0.18em] text-gold hover:text-gold"
        >
          Manage →
        </Link>
        <a
          href={link}
          target="_blank"
          rel="noreferrer"
          data-testid={`monetize-view-${sf.id}`}
          className="text-xs uppercase tracking-[0.18em] text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1"
        >
          View live <ExternalLink size={11} />
        </a>
        <button
          onClick={() => onDelete(sf)}
          data-testid={`monetize-delete-${sf.id}`}
          className="ml-auto text-neutral-400 hover:text-rose-300 transition"
          title="Delete storefront"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

function CreateStorefrontForm({ onCreated, isPending }) {
  const [title, setTitle] = useState("");
  const [tagline, setTagline] = useState("");
  const [templateKey, setTemplateKey] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (isPending) return;
    if (!title.trim()) {
      toast.error("Give your storefront a title first.");
      return;
    }
    setBusy(true);
    try {
      const sf = await monetize.createStorefront({
        title: title.trim(),
        tagline: tagline.trim(),
        template_key: templateKey || null,
      });
      toast.success(templateKey ? "Storefront created with starter products." : "Storefront created.");
      onCreated(sf);
    } catch (err) {
      toast.error(extractFormError(err, "Couldn't create storefront."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="monetize-create-form" className="border border-gold/25 bg-gold/[0.04] p-6 sm:p-8">
      <p className="overline">Open your storefront</p>
      <h2 className="font-serif text-2xl sm:text-3xl mt-3 leading-snug">
        Start with a template <span className="italic text-aurora">or a blank canvas.</span>
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Storefront title (e.g. The Aesthetic Vault)"
          maxLength={80}
          data-testid="monetize-title"
          className="bg-white border border-neutral-200 focus:border-gold focus:outline-none px-4 py-3 text-sm"
        />
        <input
          type="text"
          value={tagline}
          onChange={(e) => setTagline(e.target.value)}
          placeholder="One-line tagline (optional)"
          maxLength={120}
          data-testid="monetize-tagline"
          className="bg-white border border-neutral-200 focus:border-gold focus:outline-none px-4 py-3 text-sm"
        />
      </div>
      <div className="mt-5">
        <p className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-3">Template (optional - pre-fills 4 products)</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {Object.entries(TEMPLATE_LABELS).map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setTemplateKey(templateKey === k ? "" : k)}
              data-testid={`monetize-template-${k}`}
              aria-pressed={templateKey === k}
              className={`text-left border p-4 transition ${
                templateKey === k
                  ? "border-gold bg-gold/[0.06]"
                  : "border-neutral-200 bg-white hover:border-[#D4AF37]/50"
              }`}
            >
              <p className={`text-xs uppercase tracking-[0.18em] ${templateKey === k ? "text-gold" : "text-neutral-500"}`}>
                {label.split(" - ")[0]}
              </p>
              <p className="text-sm text-neutral-600 mt-1.5 leading-relaxed">{label.split(" - ")[1]}</p>
            </button>
          ))}
        </div>
      </div>
      <button
        type="submit"
        data-testid="monetize-create-submit"
        disabled={busy || isPending}
        className="btn-aurora mt-6 disabled:opacity-60"
      >
        <Plus size={14} /> {busy ? "Creating..." : "Create storefront"}
      </button>
      {isPending && (
        <p className="text-xs text-neutral-500 mt-3">
          Monetization Hub activates when your plan does.{" "}
          <Link to="/pricing" className="text-gold hover:text-gold">See pricing</Link>.
        </p>
      )}
    </form>
  );
}

export default function MonetizeHome() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [storefronts, setStorefronts] = useState(null);
  const isPending = (user?.plan || "pending") === "pending" && user?.role !== "admin";

  const load = async () => {
    try {
      const [a, s] = await Promise.all([monetize.analytics(), monetize.listStorefronts()]);
      setAnalytics(a);
      setStorefronts(s);
    } catch (err) {
      setStorefronts([]);
      toast.error(extractFormError(err, "Couldn't load Monetization Hub."));
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (sf) => {
    if (!window.confirm(`Delete "${sf.title}" and all its products? This cannot be undone.`)) return;
    try {
      await monetize.deleteStorefront(sf.id);
      setStorefronts((arr) => (arr || []).filter((x) => x.id !== sf.id));
      toast.success("Storefront deleted.");
    } catch (err) {
      toast.error(extractFormError(err, "Couldn't delete storefront."));
    }
  };

  const rev = analytics ? (analytics.revenue_cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" }) : "$0";

  return (
    <DashboardShell back="/dashboard" backLabel="Dashboard">
      <section data-testid="monetize-hero" className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <p className="overline">Monetization Hub</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05] mt-4">
            Turn your audience into <span className="italic text-aurora">real revenue.</span>
          </h1>
          <p className="text-neutral-500 mt-4 max-w-2xl leading-relaxed">
            One link-in-bio storefront for downloads, courses, memberships and 1:1 bookings.
            Pre-filled by industry, powered by your Content Studio, priced for your audience.
          </p>
        </div>
      </section>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5 mt-10">
        <StatCard label="Revenue" value={rev} hint="Paid orders, all time" testid="mn-stat-rev" />
        <StatCard label="Orders" value={analytics?.orders_total ?? 0} hint={`${analytics?.orders_paid ?? 0} paid · ${analytics?.orders_pending ?? 0} pending`} testid="mn-stat-orders" />
        <StatCard label="Products" value={analytics?.products ?? 0} hint="Across your storefronts" testid="mn-stat-products" />
        <StatCard label="Storefronts" value={analytics?.storefronts ?? 0} hint={`${analytics?.conversion_pct ?? 0}% conversion`} testid="mn-stat-stores" />
      </section>

      {!isPending && (storefronts?.length ?? 0) === 0 && (
        <section className="mt-10">
          <CreateStorefrontForm onCreated={(sf) => setStorefronts((arr) => [sf, ...(arr || [])])} isPending={isPending} />
        </section>
      )}

      {isPending && (
        <section data-testid="monetize-paywall" className="mt-10 border border-gold/30 bg-gold/[0.04] p-6 sm:p-7">
          <p className="overline">Pick a plan to unlock</p>
          <p className="text-neutral-600 mt-3 leading-relaxed max-w-2xl">
            The Monetization Hub activates with any paid plan. Sell in minutes.
          </p>
          <Link to="/pricing" data-testid="monetize-paywall-cta" className="btn-ghost mt-5 inline-flex">See plans</Link>
        </section>
      )}

      <section className="mt-12">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-5">
          <p className="overline">Your storefronts</p>
          {(storefronts?.length ?? 0) > 0 && !isPending && (
            <details className="text-xs">
              <summary className="text-neutral-500 hover:text-neutral-900 cursor-pointer" data-testid="monetize-new-toggle">
                + New storefront
              </summary>
              <div className="mt-4 min-w-[320px] sm:min-w-[600px]">
                <CreateStorefrontForm onCreated={(sf) => setStorefronts((arr) => [sf, ...(arr || [])])} isPending={isPending} />
              </div>
            </details>
          )}
        </div>
        {storefronts === null && <div className="text-neutral-500 text-sm">Loading your storefronts...</div>}
        {storefronts && storefronts.length > 0 && (
          <div data-testid="monetize-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {storefronts.map((sf) => (
              <StorefrontCard key={sf.id} sf={sf} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </section>

      <section className="mt-16 border-t border-neutral-200/70 pt-10">
        <p className="overline">What can I sell?</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
          {[
            { k: "download", icon: ShoppingBag, label: "Downloads", desc: "Ebooks, templates, checklists" },
            { k: "course", icon: Sparkles, label: "Courses", desc: "Multi-module video or text" },
            { k: "membership", icon: TrendingUp, label: "Memberships", desc: "Recurring monthly access" },
            { k: "booking", icon: Users, label: "Bookings", desc: "1:1 or group calendar slots" },
            { k: "lead_magnet", icon: DollarSign, label: "Lead Magnets", desc: "Free opt-ins that grow your list" },
          ].map((x) => (
            <div key={x.k} className="border border-neutral-200 bg-white p-5">
              <x.icon size={18} className="text-gold" strokeWidth={1.4} />
              <p className="font-serif text-lg mt-3">{x.label}</p>
              <p className="text-xs text-neutral-500 mt-1.5 leading-relaxed">{x.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </DashboardShell>
  );
}
